/**
 * Métricas de progreso.
 *
 * Funciones PURAS, derivadas de las sesiones y del banco. NO existe una tabla
 * de estadísticas semanales y no debe existir: sería una segunda fuente de
 * verdad que se desincroniza en cuanto alguien edite una sesión.
 *
 * Regla de honestidad que atraviesa el archivo: lo que no se midió no se
 * inventa. Un área sin datos no vale cero, y una sesión sin duración real no
 * se rellena con la planificada.
 */

import { AREAS, type Area } from '../modelo/areas.js';
import { estaActivo, type ElementoBanco } from '../modelo/banco.js';
import { cuentaComoHecha, type SesionRegistrada } from '../modelo/sesion.js';
import { CLAVES_DIA_ORDENADAS } from './progresion.js';

const SESIONES_POR_CICLO = CLAVES_DIA_ORDENADAS.length;

/** Sesiones que cuentan: completadas o parciales, nunca borradas ni abiertas. */
export function sesionesHechas(sesiones: SesionRegistrada[]): SesionRegistrada[] {
  return sesiones.filter(cuentaComoHecha);
}

export interface MinutosEntrenados {
  /** Minutos REALES, redondeados hacia abajo. */
  minutos: number;
  /**
   * Sesiones hechas que no registraron duración real.
   *
   * §16: los minutos planificados NO se suman como si se hubieran entrenado.
   * Una sesión sin duración real aporta 0 y se cuenta aquí, para que el
   * número de arriba se pueda leer sabiendo qué le falta. Mezclarlos sin
   * distinguir daría un total más bonito y menos cierto.
   */
  sesionesSinDuracion: number;
}

export function minutosEntrenados(sesiones: SesionRegistrada[]): MinutosEntrenados {
  const hechas = sesionesHechas(sesiones);
  const segundos = hechas.reduce((total, s) => total + Math.max(0, s.segundosReales ?? 0), 0);
  return {
    minutos: Math.floor(segundos / 60),
    sesionesSinDuracion: hechas.filter((s) => !(s.segundosReales > 0)).length,
  };
}

/**
 * Conversaciones con una PERSONA, nunca con el respaldo de IA.
 *
 * §17. Contar el respaldo aquí haría subir el número justo las semanas en que
 * no se consiguió a nadie, que es exactamente lo que hay que poder ver.
 */
export function conversacionesHumanas(sesiones: SesionRegistrada[]): number {
  return sesionesHechas(sesiones).filter((s) => s.conPersonaReal === true).length;
}

/** Veces que se recurrió a ChatGPT por no conseguir a nadie. Se muestra aparte. */
export function respaldosIA(sesiones: SesionRegistrada[]): number {
  return sesionesHechas(sesiones).filter(
    (s) => s.conPersonaReal === false && s.usoRespaldoIA === true,
  ).length;
}

/** Cuántos días del ciclo en curso están hechos, sobre 7. */
export function progresoDelCiclo(
  sesiones: SesionRegistrada[],
  semana: number,
): { hechos: number; total: number } {
  const hechos = new Set(
    sesionesHechas(sesiones)
      .filter((s) => s.tipo === 'dia' && s.semana === semana)
      .map((s) => s.sessionKey),
  );
  return { hechos: hechos.size, total: SESIONES_POR_CICLO };
}

export interface EstadoBanco {
  activos: number;
  dominados: number;
  archivados: number;
}

/**
 * Material activo frente a dominado.
 *
 * §19: es una métrica de MATERIAL TRABAJADO, no de nivel. Más dominados no
 * significa mejor inglés; significa que se trabajaron más cosas. La interfaz
 * lo dice con esas palabras.
 */
export function estadoDelBanco(banco: ElementoBanco[]): EstadoBanco {
  const vivos = banco.filter((e) => !e.deleted);
  return {
    activos: vivos.filter(estaActivo).length,
    dominados: vivos.filter((e) => e.estado === 'dominado').length,
    archivados: vivos.filter((e) => e.estado === 'archivado').length,
  };
}

// ── Áreas recurrentes ──────────────────────────────────────────────────────

export interface RecuentoDeArea {
  area: Area;
  /** Elementos activos del banco con esa área. */
  elementos: number;
  /** Veces marcada como «lo más difícil de hoy». */
  masDificil: number;
  /** Correcciones de ChatGPT guardadas con esa área. */
  correcciones: number;
  total: number;
}

/**
 * Qué áreas aparecen más, y de dónde sale cada número.
 *
 * §18: esto NO es una segunda puntuación oculta. Es un recuento, sin pesos y
 * sin fórmula, y la interfaz enseña los tres sumandos por separado para que se
 * pueda comprobar. La única puntuación del proyecto es la del Día 5.
 */
export function areasRecurrentes(
  sesiones: SesionRegistrada[],
  banco: ElementoBanco[],
): RecuentoDeArea[] {
  const hechas = sesionesHechas(sesiones);
  const vivos = banco.filter((e) => !e.deleted && estaActivo(e));

  const recuentos = AREAS.map((area) => {
    const elementos = vivos.filter((e) => e.area === area).length;
    const masDificil = hechas.filter((s) => s.masDificilArea === area).length;
    const correcciones = vivos.filter(
      (e) => e.area === area && e.fuente === 'chatgpt',
    ).length;
    return {
      area,
      elementos,
      masDificil,
      correcciones,
      total: elementos + masDificil + correcciones,
    };
  });

  return recuentos
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total || AREAS.indexOf(a.area) - AREAS.indexOf(b.area));
}

// ── Evolución de las autoevaluaciones ──────────────────────────────────────

export interface PuntoDeEvolucion {
  semana: number;
  /** Media de la semana, redondeada a un decimal. */
  valor: number;
  /** Cuántas sesiones aportaron el dato. Un punto de una sola sesión es débil. */
  muestras: number;
}

export interface EvolucionDeArea {
  area: Area;
  puntos: PuntoDeEvolucion[];
  /**
   * `true` con un solo punto.
   *
   * §11: con un dato se muestra el valor, no se finge una tendencia. Dos
   * puntos son una recta; uno es un punto.
   */
  soloUnDato: boolean;
}

/**
 * Media de autoevaluación por área y por semana.
 *
 * Solo sesiones-DÍA: el baseline y los checkpoints son mediciones y viven en
 * la comparación longitudinal, no en la tendencia semanal. Mezclarlos haría
 * que la curva saltara cada cuatro semanas por un cambio de tarea, no por un
 * cambio de habilidad.
 */
export function evolucionDeAutoevaluaciones(
  sesiones: SesionRegistrada[],
): EvolucionDeArea[] {
  const dias = sesionesHechas(sesiones).filter((s) => s.tipo === 'dia');

  return AREAS.map((area) => {
    const porSemana = new Map<number, number[]>();
    for (const sesion of dias) {
      const valor = sesion.autoevaluaciones[area];
      if (typeof valor !== 'number') continue;
      const lista = porSemana.get(sesion.semana) ?? [];
      lista.push(valor);
      porSemana.set(sesion.semana, lista);
    }

    const puntos = [...porSemana.entries()]
      .sort(([a], [b]) => a - b)
      .map(([semana, valores]) => ({
        semana,
        valor: Math.round((valores.reduce((a, b) => a + b, 0) / valores.length) * 10) / 10,
        muestras: valores.length,
      }));

    return { area, puntos, soloUnDato: puntos.length === 1 };
  }).filter((evolucion) => evolucion.puntos.length > 0);
}
